import {
  Alert,
  Button,
  Chip,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useEffect, useState } from 'react'
import { useCompanies, useCompanyModules, useSaveCompanyModules } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'

/**
 * The parts of the application each company runs.
 *
 * <p>Not every caterer does everything: one takes outdoor jobs and never books
 * a hall, one has no vehicles of its own, one keeps its books elsewhere. A
 * module switched off here takes its permissions with it, so its menu entry
 * disappears and the endpoints behind it refuse — there is no version of this
 * that hides the button and still answers the request.</p>
 */
export function ModulesTab() {
  const can = useAuthStore((state) => state.can)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  const platformAdmin = can('platform.admin')

  const { data: companies } = useCompanies()
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const { data: modules, error } = useCompanyModules(companyId)
  const save = useSaveCompanyModules()

  const [chosen, setChosen] = useState<string[]>([])
  const [savedAt, setSavedAt] = useState(0)

  useEffect(() => {
    setChosen((modules ?? []).filter((module) => module.enabled).map((module) => module.code))
    setSavedAt(0)
  }, [modules])

  const rows = companies ?? []
  const target = companyId ?? ownCompanyId
  const editingOwn = target === ownCompanyId

  const toggle = (code: string, on: boolean) => {
    setSavedAt(0)
    setChosen((current) =>
      on ? [...current, code] : current.filter((existing) => existing !== code),
    )
  }

  const dirty =
    (modules ?? []).some((module) => module.enabled !== chosen.includes(module.code))

  return (
    <>
      <ErrorBanner error={error ?? save.error} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          What this company runs. Switching one off removes it from everyone&rsquo;s menu and closes
          it on the server too.
        </Typography>
        {platformAdmin && rows.length > 1 ? (
          <TextField
            select
            size="small"
            label="Company"
            value={target ?? ''}
            onChange={(event) => setCompanyId(event.target.value)}
            sx={{ minWidth: 240 }}
            inputProps={{ 'aria-label': 'Company' }}
          >
            {rows.map((row) => (
              <MenuItem key={row.id} value={row.id}>
                {row.name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={90}>Runs it</TableCell>
            <TableCell width={220}>Module</TableCell>
            <TableCell>What it covers</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(modules ?? []).map((module) => {
            const on = chosen.includes(module.code)
            return (
              <TableRow key={module.code} hover sx={{ opacity: on ? 1 : 0.6 }}>
                <TableCell>
                  <Switch
                    size="small"
                    checked={on}
                    disabled={!platformAdmin}
                    onChange={(event) => toggle(module.code, event.target.checked)}
                    inputProps={{ 'aria-label': module.name }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{module.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {module.description}
                  </Typography>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {platformAdmin ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate(
                { companyId: editingOwn ? undefined : target, enabled: chosen },
                { onSuccess: () => setSavedAt(Date.now()) },
              )
            }
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {savedAt ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CheckCircleIcon fontSize="small" color="success" />
              <Typography variant="caption" color="success.main">
                Saved
              </Typography>
            </Stack>
          ) : null}
          <Chip size="small" variant="outlined" label={`${chosen.length} on`} />
        </Stack>
      ) : null}

      <Alert severity="info" sx={{ mt: 2 }}>
        Settings, outlets and users are always on — a company that could switch those off would have
        no way to switch anything back on.
        {editingOwn ? (
          <>
            {' '}
            You are editing your own company, so anything switched off disappears from your menu as
            soon as you save.
          </>
        ) : null}
      </Alert>
    </>
  )
}
